/**
 * Settings page — currently houses Change Password.
 *
 * Security UX:
 *  - Validates strength client-side (length + character classes) AND server-side
 *  - Confirms passwords match before submit
 *  - On success: stores the new token returned by the server, signs the user
 *    OUT, and redirects to landing so they re-authenticate with the new
 *    credentials. All other devices/tabs lose their session via token_version
 *    bump on the backend.
 *
 * © 2026 A-1 Affiliate Marketing LLC
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import './Settings.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const scorePassword = (pw) => {
  if (!pw) return { score: 0, label: '' };
  let score = 0;
  if (pw.length >= 12) score += 1;
  if (pw.length >= 16) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  const labels = ['Too short', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'];
  return { score, label: labels[score] || '' };
};

const Settings = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const strength = scorePassword(newPassword);
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const formValid =
    currentPassword.length > 0 &&
    newPassword.length >= 12 &&
    strength.score >= 2 &&
    passwordsMatch &&
    currentPassword !== newPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValid || submitting) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Could not change password.');
        setSubmitting(false);
        return;
      }
      toast.success(data.message || 'Password changed. Please sign in again.');
      // Force re-auth so the user proves they remember the new password.
      // Other devices are already invalidated by the server-side token_version bump.
      localStorage.clear();
      if (typeof onLogout === 'function') onLogout();
      setTimeout(() => navigate('/', { replace: true }), 800);
    } catch (err) {
      toast.error('Network error — please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="settings-page" data-testid="settings-page">
      <div className="settings-container">
        <button
          type="button"
          className="settings-back"
          onClick={() => navigate('/dashboard')}
          data-testid="settings-back"
        >
          &larr; Back to dashboard
        </button>

        <h1 className="settings-title">Account Settings</h1>
        <p className="settings-user">
          Signed in as <strong>{user?.email}</strong>
        </p>

        <section className="settings-section" data-testid="change-password-section">
          <h2>Change Password</h2>
          <p className="settings-help">
            Use a unique 12+ character password. After you save, you&apos;ll be
            signed out everywhere and asked to sign in again with the new password.
          </p>

          <form onSubmit={handleSubmit} className="settings-form">
            <label className="settings-label">
              <span>Current password</span>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="current-password-input"
              />
            </label>

            <label className="settings-label">
              <span>New password</span>
              <div className="settings-pw-row">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={12}
                  required
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  className="settings-pw-toggle"
                  onClick={() => setShowNew((v) => !v)}
                  data-testid="toggle-new-password"
                >
                  {showNew ? 'Hide' : 'Show'}
                </button>
              </div>
              {/* Always-rendered strength meter (empty state before typing) so QA
                  selectors and the user can see where the indicator will appear. */}
              <div
                className={`settings-strength s-${newPassword ? strength.score : 0}`}
                data-testid="password-strength"
              >
                <div className="settings-strength-bar">
                  <div style={{ width: newPassword ? `${(strength.score / 5) * 100}%` : '0%' }} />
                </div>
                <span>{newPassword ? strength.label : 'Strength'}</span>
              </div>
            </label>

            <label className="settings-label">
              <span>Confirm new password</span>
              <input
                type={showNew ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
                data-testid="confirm-password-input"
              />
              {confirmPassword && !passwordsMatch && (
                <span className="settings-error">Passwords don&apos;t match.</span>
              )}
            </label>

            <button
              type="submit"
              className="settings-submit"
              disabled={!formValid || submitting}
              data-testid="change-password-submit"
            >
              {submitting ? 'Saving…' : 'Change Password'}
            </button>
          </form>
        </section>

        {/* ---------------------------------------------------------------
            Change Email — re-auth required, security email to old + new
           ---------------------------------------------------------------- */}
        <ChangeEmailSection
          user={user}
          onLogout={onLogout}
          navigate={navigate}
        />

        {/* ---------------------------------------------------------------
            Change Phone — re-auth + Twilio Verify code required
           ---------------------------------------------------------------- */}
        <ChangePhoneSection
          user={user}
          onLogout={onLogout}
          navigate={navigate}
        />

        {/* ---------------------------------------------------------------
            Security & Activity — audit timeline of every auth event
           ---------------------------------------------------------------- */}
        <SecurityActivitySection />

        {/* ---------------------------------------------------------------
            Delete Account — 30-day grace period, re-auth required
           ---------------------------------------------------------------- */}
        <DeleteAccountSection user={user} />
      </div>
    </div>
  );
};

// ===========================================================================
// Sub-section: Change Email
// ===========================================================================
const ChangeEmailSection = ({ user, onLogout, navigate }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const formValid =
    currentPassword.length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail) &&
    newEmail.toLowerCase() !== (user?.email || '').toLowerCase();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValid || submitting) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/auth/change-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_email: newEmail,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Could not change email.');
        setSubmitting(false);
        return;
      }
      toast.success(data.message || 'Email changed. Please sign in again.');
      // Force a clean re-auth so the new email is visible in the session.
      localStorage.clear();
      if (typeof onLogout === 'function') onLogout();
      setTimeout(() => navigate('/', { replace: true }), 800);
    } catch (err) {
      toast.error('Network error — please try again.');
      setSubmitting(false);
    }
  };

  return (
    <section className="settings-section" data-testid="change-email-section">
      <h2>Change Email</h2>
      <p className="settings-help">
        We&apos;ll send a security alert to both your old and new addresses, sign
        you out everywhere, and ask you to sign in again.
      </p>
      <form onSubmit={handleSubmit} className="settings-form">
        <label className="settings-label">
          <span>Current password</span>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            required
            data-testid="change-email-current-password"
          />
        </label>
        <label className="settings-label">
          <span>New email address</span>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            autoComplete="email"
            required
            placeholder="new@email.com"
            data-testid="change-email-new-input"
          />
        </label>
        <button
          type="submit"
          className="settings-submit"
          disabled={!formValid || submitting}
          data-testid="change-email-submit"
        >
          {submitting ? 'Saving…' : 'Change Email'}
        </button>
      </form>
    </section>
  );
};

// ===========================================================================
// Sub-section: Change Phone (2-step — send code → verify + save)
// ===========================================================================
const ChangePhoneSection = ({ user, onLogout, navigate }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState(1); // 1 = enter new phone + request code, 2 = enter code + password
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const phoneOk = /^\+\d{7,15}$/.test(newPhone.replace(/[\s\-()]/g, ''));
  const codeOk = /^\d{4,8}$/.test(code);

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!phoneOk || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/phone/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: newPhone }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Could not send code.');
        setSending(false);
        return;
      }
      toast.success('Verification code sent.');
      setStep(2);
    } catch (err) {
      toast.error('Network error — please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!codeOk || currentPassword.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/auth/change-phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_phone: newPhone,
          verification_code: code,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Could not change phone number.');
        setSubmitting(false);
        return;
      }
      toast.success(data.message || 'Phone number changed.');
      localStorage.clear();
      if (typeof onLogout === 'function') onLogout();
      setTimeout(() => navigate('/', { replace: true }), 800);
    } catch (err) {
      toast.error('Network error — please try again.');
      setSubmitting(false);
    }
  };

  return (
    <section className="settings-section" data-testid="change-phone-section">
      <h2>Change Phone Number</h2>
      <p className="settings-help">
        Current: <strong>{user?.phone_number || '(none on file)'}</strong>. We&apos;ll
        send a verification code to the new number and an email security
        alert to your account address.
      </p>

      {step === 1 && (
        <form onSubmit={handleSendCode} className="settings-form">
          <label className="settings-label">
            <span>New phone number (with country code, e.g. +15551234567)</span>
            <input
              type="tel"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              autoComplete="tel"
              required
              placeholder="+15551234567"
              data-testid="change-phone-new-input"
            />
          </label>
          <button
            type="submit"
            className="settings-submit"
            disabled={!phoneOk || sending}
            data-testid="change-phone-send-code"
          >
            {sending ? 'Sending…' : 'Send Verification Code'}
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleSubmit} className="settings-form">
          <label className="settings-label">
            <span>Verification code (sent to {newPhone})</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
              autoComplete="one-time-code"
              required
              placeholder="123456"
              maxLength={8}
              data-testid="change-phone-code-input"
            />
          </label>
          <label className="settings-label">
            <span>Current password (confirm it&apos;s really you)</span>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="change-phone-current-password"
            />
          </label>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              type="button"
              className="settings-submit"
              style={{ background: 'transparent', border: '1px solid rgba(200,134,10,0.4)' }}
              onClick={() => { setStep(1); setCode(''); setCurrentPassword(''); }}
              data-testid="change-phone-back"
            >
              ← Use different number
            </button>
            <button
              type="submit"
              className="settings-submit"
              disabled={!codeOk || currentPassword.length === 0 || submitting}
              data-testid="change-phone-submit"
            >
              {submitting ? 'Saving…' : 'Verify & Change Phone'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

// ===========================================================================
// Sub-section: Delete Account (30-day grace period, re-auth gated)
// ===========================================================================
const DeleteAccountSection = ({ user }) => {
  const [confirming, setConfirming] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // null | "scheduled"

  const pendingAt = user?.pending_deletion_at ? new Date(user.pending_deletion_at) : null;
  const isAdmin = !!user?.is_admin;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !acknowledged || currentPassword.length === 0) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/auth/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          reason,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Could not schedule deletion.');
        setSubmitting(false);
        return;
      }
      toast.success(data.message);
      setDone(data.pending_deletion_at || 'scheduled');
    } catch (err) {
      toast.error('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (isAdmin) {
    return (
      <section className="settings-section" data-testid="delete-account-section">
        <h2 style={{ color: '#e53e3e' }}>Delete Account</h2>
        <p className="settings-help">
          The owner / admin account cannot be deleted via the self-serve flow.
        </p>
      </section>
    );
  }

  if (pendingAt && !done) {
    return (
      <section className="settings-section" data-testid="delete-account-pending">
        <h2 style={{ color: '#e53e3e' }}>Account Scheduled for Deletion</h2>
        <p className="settings-help">
          Your account will be permanently deleted on{' '}
          <strong style={{ color: '#e53e3e' }}>{pendingAt.toLocaleString()}</strong>.
          Until then, every feature still works — you can cancel anytime by
          clicking the link we emailed you.
        </p>
      </section>
    );
  }

  if (done) {
    return (
      <section className="settings-section" data-testid="delete-account-success">
        <h2>Deletion Scheduled</h2>
        <p className="settings-help">
          ✓ We&apos;ve emailed you a cancellation link. You have 30 days to
          change your mind — your account remains fully functional during
          that time.
        </p>
      </section>
    );
  }

  return (
    <section className="settings-section" data-testid="delete-account-section">
      <h2 style={{ color: '#e53e3e' }}>Delete Account</h2>
      <p className="settings-help">
        Deletion is scheduled <strong>30 days out</strong>. During that time
        you can cancel anytime by clicking the link in the confirmation email.
        After 30 days your data is permanently removed — emails, conversation
        history, referral credit, billing history. No recovery after that.
      </p>

      {!confirming && (
        <button
          type="button"
          className="settings-submit"
          style={{ background: 'rgba(229,62,62,0.12)', border: '1px solid #e53e3e', color: '#e53e3e' }}
          onClick={() => setConfirming(true)}
          data-testid="delete-account-begin"
        >
          Begin Deletion…
        </button>
      )}

      {confirming && (
        <form onSubmit={handleSubmit} className="settings-form" style={{ marginTop: '1rem' }}>
          <label className="settings-label">
            <span>Current password (confirm it&apos;s really you)</span>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="delete-account-password"
            />
          </label>
          <label className="settings-label">
            <span>Why are you leaving? (optional — helps us improve)</span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              placeholder="e.g. switched to another tool, no longer needed"
              maxLength={500}
              data-testid="delete-account-reason"
            />
          </label>
          <div className="checkbox-group" style={{ marginBottom: '0.75rem' }}>
            <input
              type="checkbox"
              id="delete-ack"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              data-testid="delete-account-ack"
            />
            <label htmlFor="delete-ack" style={{ color: '#e53e3e' }}>
              I understand my data will be permanently deleted in 30 days and there is no recovery after that.
            </label>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              type="button"
              className="settings-submit"
              style={{ background: 'transparent', border: '1px solid rgba(200,134,10,0.4)' }}
              onClick={() => { setConfirming(false); setCurrentPassword(''); setReason(''); setAcknowledged(false); }}
              data-testid="delete-account-cancel"
            >
              ← Keep my account
            </button>
            <button
              type="submit"
              className="settings-submit"
              style={{ background: '#e53e3e', border: '1px solid #e53e3e', color: '#fff' }}
              disabled={!acknowledged || currentPassword.length === 0 || submitting}
              data-testid="delete-account-confirm"
            >
              {submitting ? 'Scheduling…' : 'Schedule Deletion'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

// ===========================================================================
// Sub-section: Security & Activity (read-only audit timeline)
// ===========================================================================
const SecurityActivitySection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${BACKEND_URL}/api/auth/security-events`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setEvents(data.events || []);
      } catch (e) {
        setError('Could not load activity history.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fmtTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const m = Math.round(diff / 60_000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.round(h / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  const visible = expanded ? events : events.slice(0, 8);
  const failedLogins = events.filter((e) => e.type === 'login_failed').length;

  return (
    <section className="settings-section" data-testid="security-activity-section">
      <h2>Security &amp; Activity</h2>
      <p className="settings-help">
        Every sensitive action on your account is recorded here. Review this
        list periodically — if you see something you didn&apos;t do, reset your
        password immediately.
      </p>

      {loading && (
        <p className="settings-help" data-testid="security-activity-loading">
          Loading…
        </p>
      )}

      {!loading && error && (
        <p className="auth-error" data-testid="security-activity-error">
          {error}
        </p>
      )}

      {!loading && !error && events.length === 0 && (
        <p className="settings-help" data-testid="security-activity-empty">
          No activity recorded yet.
        </p>
      )}

      {!loading && !error && events.length > 0 && (
        <>
          {failedLogins >= 3 && (
            <div
              className="auth-error"
              data-testid="security-activity-bruteforce-warning"
              style={{ marginBottom: '0.75rem' }}
            >
              ⚠ {failedLogins} failed login attempts recorded. If these
              weren&apos;t you, reset your password now.
            </div>
          )}
          <ul
            data-testid="security-events-list"
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              borderTop: '1px solid rgba(201,168,76,0.18)',
            }}
          >
            {visible.map((ev) => {
              const isFailed = ev.type === 'login_failed';
              return (
                <li
                  key={ev.id}
                  data-testid={`security-event-${ev.type}`}
                  style={{
                    padding: '0.75rem 0',
                    borderBottom: '1px solid rgba(201,168,76,0.12)',
                    display: 'grid',
                    gridTemplateColumns: '2rem 1fr auto',
                    columnGap: '0.75rem',
                    alignItems: 'center',
                    color: isFailed ? '#e5a3a3' : '#e8e0cc',
                  }}
                >
                  <span style={{ fontSize: '1.1rem', textAlign: 'center' }}>
                    {ev.icon}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.92rem' }}>{ev.label}</div>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#8a8270',
                        fontFamily: 'monospace',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      from {ev.ip}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: '0.78rem',
                      color: '#8a8270',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {fmtTime(ev.created_at)}
                  </span>
                </li>
              );
            })}
          </ul>
          {events.length > 8 && (
            <button
              type="button"
              className="auth-link-btn"
              style={{ marginTop: '0.5rem' }}
              onClick={() => setExpanded((v) => !v)}
              data-testid="security-activity-toggle"
            >
              {expanded ? '↑ Show recent only' : `↓ Show all ${events.length} events`}
            </button>
          )}
        </>
      )}
    </section>
  );
};

export default Settings;
